import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, combineLatest } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { RegionRole, User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { ModalService } from '../../services/modal.service';

/** The pages a profile can be placed on. Additive only — see the migration. */
export const PLACEMENT_OPTIONS = [
  { label: 'Funeral directors', value: 'funeral-directors' },
  { label: 'About / Team', value: 'team' },
];

type SortKey = 'name' | 'jobTitle' | 'role' | 'bio' | 'published' | 'placements';

/**
 * Website → Profiles (#184).
 *
 * Bios were backfilled to prod in September 2026 and until now no screen
 * listed them — 27 of 31 written bios were unreachable, with nothing in the
 * app able to flip `isPublicProfile`. This is that screen.
 *
 * Lists EVERY user regardless of role: the CEO and GM belong on the team page,
 * and a developer account has a profile too. Role says nothing about where a
 * profile is published, which is the whole point of `placements`.
 *
 * Two independent switches, shown as separate columns because they are
 * routinely confused:
 *   Published  — users.isPublicProfile, the master on/off
 *   Shown on   — user_profiles.placements, WHERE it appears
 * A published profile with no placement renders nowhere. Deliberate (Ivan,
 * 2026-09-30), and called out in words so nobody has to work out why a
 * published profile is missing from the site.
 */
@Component({
  selector: 'app-content-profile-list',
  templateUrl: './content-profile-list.component.html',
})
export class ContentProfileListComponent implements OnInit, OnDestroy {
  loading = true;
  users: User[] = [];
  filtered: User[] = [];
  search = '';
  placementOptions = PLACEMENT_OPTIONS;

  roles: RegionRole[] = [];
  roleFilter: number | null = null;

  sortKey: SortKey = 'name';
  sortAsc = true;

  private roleNames = new Map<number, string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private modalService: ModalService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    /* retrieveAll() is "every OTHER user" — generateUsers filters out the
       authenticated one, because its real job is feeding the messaging list.
       Using it here hid the signed-in Content Manager from their own profile
       screen. allUsers$ is the same fetch with the current user prepended. */
    combineLatest([this.userService.retrieveAll(), this.userService.getRoles()])
      .pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([, roles]) => {
          this.roles = (roles || []).slice().sort((a, b) => a.name.localeCompare(b.name));
          this.roleNames = new Map(this.roles.map((r) => [r.id, r.name]));
          this.users = (this.userService.allUsersSourceValue || [])
            .map((u: any) => u?.data as User)
            .filter((u) => !!u && !!u.email);
          this.applyFilter();
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** Role names for the chip column — a user can hold several. */
  roleLabels(u: User): string[] {
    return (u.roles || []).map((id) => this.roleNames.get(id)).filter((n): n is string => !!n);
  }

  applyFilter(): void {
    const q = this.search.trim().toLowerCase();
    const needles = q ? q.split(/\s+/).filter(Boolean) : [];
    let rows = this.users.filter((u) => {
      if (this.roleFilter != null && !(u.roles || []).includes(this.roleFilter)) return false;
      if (!needles.length) return true;
      // Email is searchable because several accounts share a first name and
      // the +suffix is often the only thing that tells them apart.
      const hay = `${u.firstName} ${u.lastName} ${u.email} ${u.jobTitle || ''} ${this.roleLabels(u).join(' ')}`.toLowerCase();
      return needles.every((n) => hay.includes(n));
    });
    rows = rows.sort((a, b) => this.compare(a, b) * (this.sortAsc ? 1 : -1));
    this.filtered = rows;
  }

  sortBy(key: SortKey): void {
    if (this.sortKey === key) this.sortAsc = !this.sortAsc;
    else {
      this.sortKey = key;
      this.sortAsc = true;
    }
    this.applyFilter();
  }

  sortIcon(key: SortKey): string {
    if (this.sortKey !== key) return 'pi-sort';
    return this.sortAsc ? 'pi-sort-up' : 'pi-sort-down';
  }

  private compare(a: User, b: User): number {
    switch (this.sortKey) {
      case 'jobTitle':
        return (a.jobTitle || '').localeCompare(b.jobTitle || '');
      case 'role':
        return this.roleLabels(a).join(' ').localeCompare(this.roleLabels(b).join(' '));
      case 'bio':
        return Number(this.hasBio(b)) - Number(this.hasBio(a));
      case 'published':
        return Number(!!b.isPublicProfile) - Number(!!a.isPublicProfile);
      case 'placements':
        return this.placementLabels(a).join(' ').localeCompare(this.placementLabels(b).join(' '));
      default:
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    }
  }

  /** Labels for the placement chips; empty renders the warning instead. */
  placementLabels(u: User): string[] {
    const set = new Set(u.placements || []);
    return PLACEMENT_OPTIONS.filter((o) => set.has(o.value)).map((o) => o.label);
  }

  /**
   * Either half of the pair missing means the profile is NOT on the site.
   * Both cases get called out, because the two columns read as a promise
   * otherwise: "Shown on: Funeral directors" next to "Published: No" looks
   * like the person is listed, and they are not.
   */
  isStranded(u: User): boolean {
    return !!u.isPublicProfile && (u.placements || []).length === 0;
  }

  /** Placed on a page but never published — the backfill's common case. */
  isUnpublished(u: User): boolean {
    return !u.isPublicProfile && (u.placements || []).length > 0;
  }

  /** True when this profile actually renders somewhere on picaluna.com. */
  isLive(u: User): boolean {
    return !!u.isPublicProfile && (u.placements || []).length > 0;
  }

  hasBio(u: User): boolean {
    return !!(u.bio && String(u.bio).trim());
  }

  edit(u: User): void {
    const dialog = this.modalService.editProfile({ user: u });
    dialog.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (saved: User | undefined) => {
        if (!saved) return;
        const i = this.users.findIndex((x) => x.id === saved.id);
        if (i !== -1) this.users[i] = { ...this.users[i], ...saved };
        this.applyFilter();
      },
    });
  }
}
